import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  IonPage,
  IonContent,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
} from "@ionic/react";
import { IonModal } from "@ionic/react";
import { Save, Trash2, ArrowRight, ArrowLeft, Check, ExternalLink, Lock, Link2, Plus, X } from "lucide-react";
import { useStore } from "../store/useStore";
import type { TaskUrgency, TaskImportance } from "../types";
import AddPrerequisiteModal from "../components/AddPrerequisiteModal";
import { useConfirmedComplete } from "../hooks/useConfirmedComplete";

export default function TaskDetailPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const history = useHistory();
  const tasks = useStore((state) => state.tasks);
  const sharedTasks = useStore((state) => state.sharedTasks);
  const tags = useStore((state) => state.tags);
  const user = useStore((state) => state.user);
  const updateTask = useStore((state) => state.updateTask);
  const deleteTask = useStore((state) => state.deleteTask);
  const moveToToday = useStore((state) => state.moveToToday);
  const moveToBacklog = useStore((state) => state.moveToBacklog);
  const dependencies = useStore((state) => state.dependencies);
  const allTasks = useStore((state) => state.tasks);
  const removeDependency = useStore((state) => state.removeDependency);
  const { requestComplete, modal: completionModal } = useConfirmedComplete();

  // Look up task in both own tasks and shared tasks
  const task = tasks.find((t) => t.uuid === taskId) || sharedTasks.find((t) => t.uuid === taskId);
  const isShared = task && user ? task.user_uuid !== user.id : false;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [urgency, setUrgency] = useState<TaskUrgency | "">("");
  const [importance, setImportance] = useState<TaskImportance | "">("");
  const [url, setUrl] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [tagId, setTagId] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [prereqModalOpen, setPrereqModalOpen] = useState(false);

  // Dependency rows where this task is the dependent (its prerequisites)
  const prerequisiteDeps = useMemo(
    () => dependencies.filter((d) => d.dependent_uuid === taskId),
    [dependencies, taskId]
  );
  // Dependency rows where this task is a prerequisite (the things it unblocks)
  const dependentDeps = useMemo(
    () => dependencies.filter((d) => d.prerequisite_uuid === taskId),
    [dependencies, taskId]
  );
  const byUuid = useMemo(() => new Map(allTasks.map((t) => [t.uuid, t])), [allTasks]);

  const incompletePrereqCount = prerequisiteDeps.filter((d) => {
    const t = byUuid.get(d.prerequisite_uuid);
    return t && t.status !== "done" && t.status !== "archived";
  }).length;

  useEffect(() => {
    if (!task) return;
    setTitle(task.title || task.raw_input);
    setDescription(task.description || "");
    setUrgency(task.urgency || "");
    setImportance(task.importance || "");
    setUrl(task.url || "");
    setDueDate(task.due_date || "");
    setTagId(task.tag_id);
    setIsDirty(false);
  }, [task]);

  const handleSave = useCallback(async () => {
    if (!task) return;
    await updateTask(task.uuid, {
      title: title || null,
      description: description || null,
      urgency: (urgency || null) as TaskUrgency | null,
      importance: (importance || null) as TaskImportance | null,
      url: url || null,
      due_date: dueDate || null,
      tag_id: tagId,
    });
    setIsDirty(false);
  }, [task, updateTask, title, description, urgency, importance, url, dueDate, tagId]);

  const handleDelete = useCallback(async () => {
    await deleteTask(taskId);
    history.goBack();
  }, [deleteTask, taskId, history]);

  if (!task) {
    return (
      <IonPage>
        <IonContent>
          <div className="p-4 text-center text-muted-foreground">
            Task not found
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/dump" />
          </IonButtons>
          <IonTitle>Task</IonTitle>
          <IonButtons slot="end">
            {isDirty && !isShared && (
              <button onClick={handleSave} className="p-2 text-primary">
                <Save className="w-5 h-5" />
              </button>
            )}
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="p-4 space-y-6">
          {/* Shared task banner */}
          {isShared && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
              <Lock className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
              <span className="text-sm text-blue-700 dark:text-blue-300">
                Shared task — read only
              </span>
            </div>
          )}

          {/* Raw input */}
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Original input
            </label>
            <p className="text-sm text-muted-foreground mt-1 italic">
              {task.raw_input}
            </p>
          </div>

          {/* Title */}
          <div>
            <label htmlFor="task-title" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Title
            </label>
            <input
              id="task-title"
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setIsDirty(true);
              }}
              disabled={isShared}
              className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 disabled:cursor-not-allowed"
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                setIsDirty(true);
              }}
              rows={4}
              disabled={isShared}
              className="mt-1 w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 disabled:cursor-not-allowed"
            />
          </div>

          {/* URL */}
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              URL
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                setIsDirty(true);
              }}
              placeholder="https://..."
              disabled={isShared}
              className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 disabled:cursor-not-allowed"
            />
            {url && (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary mt-1 hover:underline"
              >
                <ExternalLink className="w-3 h-3" />
                Open link
              </a>
            )}
          </div>

          {/* Due date */}
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Due date
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => {
                setDueDate(e.target.value);
                setIsDirty(true);
              }}
              disabled={isShared}
              className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 disabled:cursor-not-allowed"
            />
          </div>

          {/* Urgency + Importance */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="task-urgency" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Urgency
              </label>
              <select
                id="task-urgency"
                value={urgency}
                onChange={(e) => {
                  setUrgency(e.target.value as TaskUrgency | "");
                  setIsDirty(true);
                }}
                disabled={isShared}
                className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <option value="">None</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div>
              <label htmlFor="task-importance" className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Importance
              </label>
              <select
                id="task-importance"
                value={importance}
                onChange={(e) => {
                  setImportance(e.target.value as TaskImportance | "");
                  setIsDirty(true);
                }}
                disabled={isShared}
                className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <option value="">None</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>

          {/* Tag selector (own tasks only) */}
          {!isShared && tags.length > 0 && (
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Tag
              </label>
              <select
                value={tagId || ""}
                onChange={(e) => {
                  setTagId(e.target.value || null);
                  setIsDirty(true);
                }}
                className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">None</option>
                {tags.map((tag) => (
                  <option key={tag.id} value={tag.id}>
                    {tag.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Tag display for shared tasks */}
          {isShared && task.tag_id && (
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Tag
              </label>
              <p className="text-sm mt-1 capitalize">
                {tags.find((t) => t.id === task.tag_id)?.name || task.tag_id}
              </p>
            </div>
          )}

          {/* Prerequisites section (own tasks only) */}
          {!isShared && (
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Prerequisites
                </label>
                <button
                  onClick={() => setPrereqModalOpen(true)}
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  <Plus className="w-3 h-3" />
                  Add prerequisite
                </button>
              </div>

              {prerequisiteDeps.length === 0 ? (
                <p className="text-xs text-muted-foreground mt-2 italic">
                  No prerequisites. Add one if this task depends on something being done first.
                </p>
              ) : (
                <ul className="mt-2 space-y-1.5">
                  {prerequisiteDeps.map((dep) => {
                    const prereq = byUuid.get(dep.prerequisite_uuid);
                    if (!prereq) return null;
                    const isDone = prereq.status === "done" || prereq.status === "archived";
                    return (
                      <li
                        key={dep.uuid}
                        className="flex items-center gap-2 p-2 rounded-lg border border-border bg-surface"
                      >
                        {isDone ? (
                          <div className="w-4 h-4 rounded-full bg-success/20 flex items-center justify-center flex-shrink-0">
                            <Check className="w-3 h-3 text-success" />
                          </div>
                        ) : (
                          <Link2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                        )}
                        <button
                          onClick={() => history.push(`/tasks/${prereq.uuid}`)}
                          className={`flex-1 text-left text-sm truncate ${
                            isDone ? "text-muted-foreground line-through" : "text-foreground hover:text-primary"
                          }`}
                        >
                          {prereq.title || prereq.raw_input}
                        </button>
                        <button
                          onClick={() => removeDependency(dep.uuid)}
                          className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 flex-shrink-0"
                          aria-label="Remove prerequisite"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}

              {dependentDeps.length > 0 && (
                <div className="mt-3">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Prerequisite of
                  </label>
                  <ul className="mt-2 space-y-1.5">
                    {dependentDeps.map((dep) => {
                      const parent = byUuid.get(dep.dependent_uuid);
                      if (!parent) return null;
                      return (
                        <li
                          key={dep.uuid}
                          className="flex items-center gap-2 p-2 rounded-lg border border-border bg-surface"
                        >
                          <Link2 className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                          <button
                            onClick={() => history.push(`/tasks/${parent.uuid}`)}
                            className="flex-1 text-left text-sm truncate text-foreground hover:text-primary"
                          >
                            {parent.title || parent.raw_input}
                          </button>
                          <button
                            onClick={() => removeDependency(dep.uuid)}
                            className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 flex-shrink-0"
                            aria-label="Remove link"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Status info */}
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Status
            </label>
            <p className="text-sm mt-1 capitalize">{task.status}</p>
          </div>

          {/* Action buttons — hidden for shared tasks */}
          {!isShared && (
            <div className="space-y-2 pt-4">
              {task.status === "backlog" && (
                <button
                  onClick={() => {
                    moveToToday(task.uuid);
                    if (incompletePrereqCount === 0) history.goBack();
                  }}
                  disabled={incompletePrereqCount > 0}
                  className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={
                    incompletePrereqCount > 0
                      ? "Complete prerequisites first"
                      : undefined
                  }
                >
                  <ArrowRight className="w-4 h-4" />
                  {incompletePrereqCount > 0
                    ? `Blocked by ${incompletePrereqCount} prerequisite${incompletePrereqCount === 1 ? "" : "s"}`
                    : "Move to Today"}
                </button>
              )}
              {task.status === "today" && (
                <>
                  <button
                    onClick={() => {
                      requestComplete(task.uuid);
                      if (incompletePrereqCount === 0) history.goBack();
                    }}
                    className="btn-success w-full flex items-center justify-center gap-2"
                  >
                    <Check className="w-4 h-4" />
                    Complete
                  </button>
                  <button
                    onClick={() => {
                      moveToBacklog(task.uuid);
                      history.goBack();
                    }}
                    className="btn-secondary w-full flex items-center justify-center gap-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Move to Backlog
                  </button>
                </>
              )}
              <button
                onClick={() => setConfirmOpen(true)}
                className="btn-danger w-full flex items-center justify-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete Task
              </button>
            </div>
          )}
          <IonModal
            isOpen={confirmOpen}
            onDidDismiss={() => setConfirmOpen(false)}
            className="confirm-delete-modal"
            initialBreakpoint={1}
            breakpoints={[0, 1]}
          >
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-destructive/10">
                  <Trash2 className="w-5 h-5 text-destructive" />
                </div>
                <h2 className="text-lg font-semibold text-foreground">Delete task?</h2>
              </div>
              <p className="text-sm text-muted-foreground line-clamp-2">
                {task.title || task.raw_input}
              </p>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setConfirmOpen(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setConfirmOpen(false);
                    handleDelete();
                  }}
                  className="btn-danger flex-1"
                >
                  Delete
                </button>
              </div>
            </div>
          </IonModal>

          {/* Metadata */}
          <div className="text-xs text-muted-foreground space-y-1 pt-4 border-t border-border">
            <p>Created: {new Date(task.created_at).toLocaleString()}</p>
            {task.ai_enriched_at && (
              <p>
                AI enriched: {new Date(task.ai_enriched_at).toLocaleString()}
              </p>
            )}
            {task.source !== "manual" && <p>Source: {task.source}</p>}
          </div>
        </div>

        {!isShared && (
          <AddPrerequisiteModal
            isOpen={prereqModalOpen}
            onClose={() => setPrereqModalOpen(false)}
            dependentTask={task}
          />
        )}

        {completionModal}
      </IonContent>
    </IonPage>
  );
}
